import { GitCompareArrows, RotateCcw, Save } from "lucide-react";
import { useMemo, useState } from "react";
import {
  editorConflictRows,
  mergeEditorConflict,
  type EditorConflictChoice,
  type EditorConflictModel,
  type EditorConflictSection,
} from "@/lib/domain/editor-conflict";
import styles from "./editor/editor-workspace.module.css";

export function EditorConflictPanel({
  base,
  local,
  server,
  busy,
  onUseServer,
  onResolve,
}: {
  base: EditorConflictModel;
  local: EditorConflictModel;
  server: EditorConflictModel;
  busy: boolean;
  onUseServer: (model: EditorConflictModel) => void;
  onResolve: (model: EditorConflictModel) => void;
}) {
  const rows = useMemo(
    () => editorConflictRows(base, local, server),
    [base, local, server],
  );
  const [choices, setChoices] = useState<
    Partial<Record<EditorConflictSection, EditorConflictChoice>>
  >({});
  const unresolved = rows.filter(
    (row) => row.requiresChoice && !choices[row.section],
  ).length;

  return (
    <section className={styles.conflictPanel} role="alert" aria-live="assertive">
      <div className={styles.conflictHeading}>
        <span className={styles.conflictIcon}><GitCompareArrows size={18} /></span>
        <div>
          <strong>Bu kayıt siz düzenlerken güncellendi</strong>
          <p>
            Başka bir kullanıcının değişiklikleri korunuyor. İki tarafta da
            değişen bölümler için hangi sürümün kullanılacağını seçin.
          </p>
        </div>
      </div>
      <div className={styles.conflictRows}>
        {rows.map((row) => (
          <div className={styles.conflictRow} key={row.section}>
            <div>
              <strong>{row.label}</strong>
              <span>
                {row.requiresChoice
                  ? "Her iki sürümde de değişti"
                  : row.localChanged
                    ? "Yalnız sizin sürümünüzde değişti"
                    : "Yalnız güncel sürümde değişti"}
              </span>
            </div>
            {row.requiresChoice ? (
              <div className={styles.conflictChoices} role="group" aria-label={`${row.label} için sürüm seçimi`}>
                <button
                  type="button"
                  aria-pressed={choices[row.section] === "server"}
                  onClick={() => setChoices((current) => ({ ...current, [row.section]: "server" }))}
                >
                  Güncel sürüm
                </button>
                <button
                  type="button"
                  aria-pressed={choices[row.section] === "local"}
                  onClick={() => setChoices((current) => ({ ...current, [row.section]: "local" }))}
                >
                  Benim sürümüm
                </button>
              </div>
            ) : (
              <span className={styles.autoMerge}>Otomatik birleştirilecek</span>
            )}
          </div>
        ))}
      </div>
      <div className="ui-actions end">
        <button
          type="button"
          className="ui-button ui-button-secondary ui-button-md"
          disabled={busy}
          onClick={() => onUseServer(server)}
        >
          <RotateCcw size={16} /> Güncel sürümü kullan
        </button>
        <button
          type="button"
          className="ui-button ui-button-primary ui-button-md"
          disabled={busy || unresolved > 0}
          onClick={() => onResolve(mergeEditorConflict(base, local, server, choices))}
        >
          <Save size={16} />
          {unresolved > 0 ? `${unresolved} seçim kaldı` : "Birleştir ve kaydet"}
        </button>
      </div>
    </section>
  );
}
